<xemex-dialog-shell
  [title]="'installations.profile.dialog.title' | translate"
  [subtitle]="'installations.profile.dialog.lead' | translate"
  mutedFooter
>
  @if (isLoading()) {
    <xemex-spinner />
  } @else if (loadFailed()) {
    <div class="flex items-center justify-between gap-4">
      <span>{{ 'installations.profile.dialog.failed' | translate }}</span>
      <xemex-text-button icon="refresh" (click)="load()">
        {{ 'installations.insights.retry' | translate }}
      </xemex-text-button>
    </div>
  } @else {
    <div class="profile-dialog">
      <div class="profile-dialog__presets">
        <xemex-search-input
          [placeholder]="'installations.profile.dialog.searchPlaceholder' | translate"
          (valueChange)="query.set($event)"
        />
        <p class="profile-dialog__count">
          {{
            (shownOptions().length === 1
              ? 'installations.profile.dialog.profileCountOne'
              : 'installations.profile.dialog.profileCount'
            ) | translate: { count: shownOptions().length }
          }}
        </p>
        <ul class="profile-dialog__list">
          @for (option of shownOptions(); track option.code) {
            <li>
              <button
                type="button"
                class="profile-dialog__preset"
                [class.profile-dialog__preset--selected]="option.code === selected()?.code"
                [attr.aria-pressed]="option.code === selected()?.code"
                (click)="select(option.code)"
              >
                <span class="min-w-0 flex-1">
                  <span class="profile-dialog__preset-name">
                    <span class="truncate">{{ option.name }}</span>
                    @if (option.current) {
                      <xemex-tag
                        state="neutral"
                        [label]="'installations.profile.dialog.currentTag' | translate"
                      />
                    }
                  </span>
                  <span class="profile-dialog__preset-summary">
                    {{
                      'installations.profile.dialog.presetSummary'
                        | translate: { required: option.required.length, bound: option.bound }
                    }}
                  </span>
                </span>
                <mat-icon class="profile-dialog__chevron" aria-hidden="true"
                  >chevron_right</mat-icon
                >
              </button>
            </li>
          } @empty {
            <li class="profile-dialog__empty">
              {{ 'installations.profile.dialog.noMatches' | translate }}
            </li>
          }
        </ul>
      </div>

      <div class="profile-dialog__detail" aria-live="polite">
        @if (selected(); as selected) {
          <div class="flex flex-col gap-1">
            <h3 class="profile-dialog__name">{{ selected.name }}</h3>
            @if (selected.description) {
              <p class="profile-dialog__description">{{ selected.description }}</p>
            }
          </div>

          <div class="flex flex-col gap-2">
            <h4 class="profile-dialog__heading">
              {{ 'installations.profile.dialog.requiredPoints' | translate }}
              <span class="profile-dialog__heading-count">({{ selected.required.length }})</span>
            </h4>
            @if (selected.required.length) {
              <div class="profile-dialog__table" role="table">
                <div class="profile-dialog__row profile-dialog__row--head" role="row">
                  <span role="columnheader">
                    {{ 'installations.profile.dialog.columns.point' | translate }}
                  </span>
                  <span role="columnheader">
                    {{ 'installations.profile.dialog.columns.unit' | translate }}
                  </span>
                  <span role="columnheader">
                    {{ 'installations.profile.dialog.columns.status' | translate }}
                  </span>
                </div>
                @for (group of groups(); track group.key) {
                  <div class="profile-dialog__group" role="row">
                    <span role="rowheader">
                      <mat-icon aria-hidden="true">{{ group.icon }}</mat-icon>
                      {{ 'installations.roleGroups.' + group.key | translate }}
                    </span>
                  </div>
                  @for (point of group.points; track point.role) {
                    <div class="profile-dialog__row profile-dialog__point" role="row">
                      <span class="min-w-0" role="cell">
                        {{ point.label }}
                        @if (point.isNew) {
                          <span class="profile-dialog__new">
                            {{ 'installations.profile.dialog.newTag' | translate }}
                          </span>
                        }
                      </span>
                      <span class="profile-dialog__unit" role="cell">{{ point.unit }}</span>
                      <span role="cell">
                        <xemex-tag
                          [state]="point.bound ? 'success' : 'warning'"
                          [label]="
                            (point.bound
                              ? 'installations.bindingState.bound'
                              : 'installations.bindingState.unbound'
                            ) | translate
                          "
                        />
                      </span>
                    </div>
                  }
                }
              </div>
            } @else {
              <p class="profile-dialog__description">
                {{ 'installations.profile.dialog.noRequiredPoints' | translate }}
              </p>
            }
          </div>

          @if (selected.noLongerRequired.length) {
            <div class="profile-dialog__note" data-testid="no-longer-required">
              <mat-icon aria-hidden="true">info_outline</mat-icon>
              <p class="m-0">
                <strong>{{
                  'installations.profile.dialog.noLongerRequiredLabel' | translate
                }}</strong>
                {{
                  'installations.profile.dialog.noLongerRequired'
                    | translate: { points: selected.noLongerRequired.join(', ') }
                }}
              </p>
            </div>
          }

          @if (refusal(); as refusal) {
            <p class="m-0 text-text-error" role="alert">{{ refusal.messageKey | translate }}</p>
          }
        }
      </div>
    </div>
  }

  <div actions class="flex w-full flex-wrap items-center gap-3">
    @if (consequence(); as consequence) {
      <p class="profile-dialog__consequence" data-testid="profile-consequence">
        {{ consequence.before }}<strong>{{ consequence.name }}</strong
        >{{ consequence.after }}
      </p>
    }
    <span class="ml-auto flex items-center gap-3">
      <xemex-text-button (click)="cancel()">
        {{ 'general.btnCancel' | translate }}
      </xemex-text-button>
      <xemex-filled-button [disabled]="!canSave()" [loading]="isSaving()" (click)="save()">
        {{ 'installations.profile.dialog.apply' | translate }}
      </xemex-filled-button>
    </span>
  </div>
</xemex-dialog-shell>
